import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";

const WORD_NUMBERS_EN = [
  "Zero","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten",
  "Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen","Twenty",
];
const WORD_NUMBERS_FR = [
  "Zéro","Un","Deux","Trois","Quatre","Cinq","Six","Sept","Huit","Neuf","Dix",
  "Onze","Douze","Treize","Quatorze","Quinze","Seize","Dix-sept","Dix-huit","Dix-neuf","Vingt",
];

export default function ServicesGrid() {
  const { services: SERVICES, lang } = useLang();
  const words = lang === "fr" ? WORD_NUMBERS_FR : WORD_NUMBERS_EN;
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  return (
    <section
      data-testid={IDS.services.grid}
      className="relative border-t border-border bg-background"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16 items-end">
          <div className="md:col-span-8">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
              [ 02 · {lang === "fr" ? "Services" : "Services"} ]
            </div>
            <h2 className="font-serif font-light text-4xl md:text-7xl tracking-tighter leading-[0.95]">
              {words[SERVICES.length] || SERVICES.length}{" "}
              <i className="text-primary">{lang === "fr" ? "Disciplines" : "Disciplines"}</i>
              <br /> {lang === "fr" ? "Une norme." : "One Standard."}
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-foreground/70 leading-relaxed">
              {lang === "fr"
                ? "De la dissuasion en uniforme à la protection rapprochée exécutive, notre catalogue couvre chaque vecteur de menace qu'un client du Nouveau-Brunswick rencontre."
                : "From uniformed deterrence to executive close-protection, our service catalogue covers every threat vector a New Brunswick client encounters."}
            </p>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="mt-6 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase hover:text-primary transition-colors"
            >
              {lang === "fr" ? "Tous les services" : "View All Services"} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <div
          ref={ref}
          onMouseMove={onMove}
          className="relative grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 border-t border-l border-border"
          style={{
            "--x": `${pos.x}%`,
            "--y": `${pos.y}%`,
          }}
        >
          <div className="pointer-events-none absolute inset-0 spotlight" />
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        duration: 0.7,
        delay: (index % 4) * 0.08,
        ease: [0.76, 0, 0.24, 1],
      }}
      className="relative border-r border-b border-border group"
    >
      <Link
        to={`/services/${service.slug}`}
        data-testid={IDS.services.card(service.slug)}
        className="block p-8 md:p-10 h-full min-h-[280px] flex flex-col justify-between transition-colors hover:bg-[hsl(0_0%_7%)]"
      >
        <div>
          <div className="flex items-start justify-between mb-6">
            <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground">
              {String(index + 1).padStart(2, "0")}
            </span>
            <ArrowUpRight
              size={16}
              strokeWidth={1.2}
              className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
            />
          </div>
          <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight mb-4 group-hover:text-primary transition-colors">
            {service.name}
          </h3>
        </div>
        <p className="font-mono text-[11px] leading-relaxed text-muted-foreground tracking-wide">
          {service.tagline}
        </p>
      </Link>
    </motion.div>
  );
}
